import type { ChainFamily } from 'src/blockchain';
import type { ApiLocationView } from './location';
import type { ApiSignedChallenge } from './wallet';

/** Account role selected on the plans page. Mirrors api AccountRole. */
export enum AccountRole {
  PERSONAL = 'personal',
  DEALER = 'dealer',
}

/**
 * Body of POST /user/auth/register. `email` + `role` are required. A dealer names their place as
 * `locationId` — a city from `GET /public/locations` (api#298, partners-spec §3.8); the free-text
 * `region` the form used to send is gone for every plan (decisions no. 10, 11, 13).
 */
export interface ApiRegisterPayload {
  email: string;
  role: AccountRole;
  name?: string;
  locationId?: string;
  marketingConsent?: boolean;
}

/** POST /user/auth/register | login | login/resend return an OTP reference. */
export interface ApiOtpReference {
  reference: string;
}

/** Body of POST /user/auth/login. Sends an OTP to an existing account's email. */
export interface ApiUserLoginPayload {
  email: string;
}

/** Body of POST /user/auth/email/change (account session). Sends an OTP to `newEmail`. */
export interface ApiChangeEmailPayload {
  newEmail: string;
}

/**
 * POST /user/auth/email/change returns one reference per code: api mails a code to the new address
 * AND a code to the address currently on the account (api#131). Confirming needs both — the code in
 * the current mailbox is what stops a stolen session from quietly moving the account away.
 */
export interface ApiChangeEmailReferences {
  newEmailReference: string;
  currentEmailReference: string;
}

/**
 * Body of POST /user/auth/email/change/confirm: both codes with their references, plus a signature
 * by the account's primary wallet when one is linked. A purely-email account has no wallet to sign
 * with and api lets it through on the two codes alone (spec §2, the honest boundary).
 */
export interface ApiChangeEmailConfirmPayload extends ApiChangeEmailReferences {
  newEmailCode: string;
  currentEmailCode: string;
  signedChallenge?: ApiSignedChallenge;
}

/** Body of POST /user/auth/login/confirm. */
export interface ApiOtpConfirmPayload {
  code: string;
  reference: string;
}

/**
 * POST /user/auth/login/confirm with `X-Session-Transport: cookie` (api#454): the session itself
 * went out as the api's cookie, which no script sees; the body says only how long it lives.
 */
export interface ApiSessionStarted {
  expiresAt: string;
}

/**
 * POST /user/auth/logout/all — how many sessions "sign out everywhere" ended (api SessionsEndedDto).
 * Only the account's own: a linked wallet holds no session since api#407, so there is nothing of a
 * wallet's to end.
 */
export interface ApiSessionsEnded {
  /** E-mail sessions: one per device the account was signed in on, this one included. */
  user: number;
}

/** GET /user/auth/me — the authenticated account (mirror of api UserDto). */
export interface ApiUserAccount {
  id: string;
  email: string;
  name: string | null;
  role: AccountRole;
  marketingConsent: boolean;
  consentedAt: string | null;
  primaryWalletId: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string | null;
  /**
   * Wallets linked to this account, at most one per family (multichain spec §4.4). The client
   * needs them before offering a network to pay on: Buy Now moves money client-side and records
   * the purchase afterwards, so a family the account has not linked would be paid from and only
   * then refused.
   *
   * Optional because an api deployed before this field simply omits it — every read must treat a
   * missing list as "unknown", never crash on it. Verified the hard way against staging.
   */
  wallets?: ApiAccountWallet[];
  /**
   * The dealer's place and business profile (api#298, partners-spec §3.8). Optional for the same
   * reason as `wallets`: an api deployed before these fields omits them, and a read must treat a
   * missing field as unknown — never as empty. Null for a personal account, and for a dealer who has
   * not set one yet.
   */
  location?: ApiLocationView | null;
  /** When the place may be changed next (ISO 8601); null means now. */
  locationChangeAllowedAt?: string | null;
  addressLine?: string | null;
  phone?: string | null;
  website?: string | null;
  about?: string | null;
}

/** One linked wallet slot (api AccountWalletDto). */
export interface ApiAccountWallet {
  id: string;
  walletAddress: string;
  chainType: ChainFamily;
  /** The wallet that owns the account's data. */
  isPrimary: boolean;
}
